import React, { useState } from 'react';
import { Plus, Heart, Leaf, Moon, Zap, Calendar, TrendingUp, Target, Activity } from 'lucide-react';

interface HolisticTreatment {
  id: string;
  name: string;
  category: 'meditation' | 'acupuncture' | 'massage' | 'yoga' | 'supplements' | 'exercise' | 'diet' | 'sleep' | 'stress_management';
  targetCondition: string;
  frequency: string;
  duration: string;
  provider?: string;
  notes?: string;
  effectivenessRating?: number; // 1-10 scale
  lastSession: string;
  totalSessions: number;
  cost?: number;
  isActive: boolean;
}

interface TreatmentCorrelation {
  treatmentId: string;
  treatmentName: string;
  symptom: string;
  improvement: number; // percentage
  timeframe: string;
  confidence: number;
}

export const HolisticTreatmentTracker: React.FC = () => {
  const [treatments, setTreatments] = useState<HolisticTreatment[]>([
    {
      id: '1',
      name: 'Daily Meditation',
      category: 'meditation',
      targetCondition: 'Generalized Anxiety Disorder',
      frequency: 'Daily',
      duration: '20 minutes',
      effectivenessRating: 8,
      lastSession: '2024-01-15T08:00:00Z',
      totalSessions: 45,
      isActive: true,
      notes: 'Using Headspace app, focusing on breath awareness'
    },
    {
      id: '2',
      name: 'Acupuncture Therapy',
      category: 'acupuncture',
      targetCondition: 'Chronic Pain',
      frequency: 'Weekly',
      duration: '60 minutes',
      provider: 'Dr. Chen - Holistic Wellness Center',
      effectivenessRating: 7,
      lastSession: '2024-01-12T14:00:00Z',
      totalSessions: 8,
      cost: 80,
      isActive: true,
      notes: 'Focusing on lower back pain points'
    },
    {
      id: '3',
      name: 'Magnesium Supplement',
      category: 'supplements',
      targetCondition: 'Hypertension',
      frequency: 'Daily',
      duration: '400mg',
      effectivenessRating: 6,
      lastSession: '2024-01-15T20:00:00Z',
      totalSessions: 30,
      cost: 25,
      isActive: true,
      notes: 'Taking with dinner to improve absorption'
    },
    {
      id: '4',
      name: 'Progressive Muscle Relaxation',
      category: 'stress_management',
      targetCondition: 'Insomnia',
      frequency: '3x per week',
      duration: '15 minutes',
      effectivenessRating: 9,
      lastSession: '2024-01-14T22:00:00Z',
      totalSessions: 24,
      isActive: true,
      notes: 'Significant improvement in sleep quality'
    }
  ]);

  const [correlations] = useState<TreatmentCorrelation[]>([
    {
      treatmentId: '1',
      treatmentName: 'Daily Meditation',
      symptom: 'Anxiety Levels',
      improvement: 45,
      timeframe: '6 weeks',
      confidence: 87
    },
    {
      treatmentId: '2',
      treatmentName: 'Acupuncture Therapy',
      symptom: 'Back Pain Intensity',
      improvement: 60,
      timeframe: '8 sessions',
      confidence: 82
    },
    {
      treatmentId: '4',
      treatmentName: 'Progressive Muscle Relaxation',
      symptom: 'Sleep Quality',
      improvement: 70,
      timeframe: '4 weeks',
      confidence: 91
    }
  ]);

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'meditation': 'bg-purple-100 text-purple-800',
      'acupuncture': 'bg-green-100 text-green-800',
      'massage': 'bg-blue-100 text-blue-800',
      'yoga': 'bg-orange-100 text-orange-800',
      'supplements': 'bg-yellow-100 text-yellow-800',
      'exercise': 'bg-red-100 text-red-800',
      'diet': 'bg-teal-100 text-teal-800',
      'sleep': 'bg-indigo-100 text-indigo-800',
      'stress_management': 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'meditation': return Moon;
      case 'acupuncture': return Target;
      case 'massage': return Heart;
      case 'yoga': return Activity;
      case 'supplements': return Leaf;
      case 'exercise': return Zap;
      case 'sleep': return Moon;
      case 'stress_management': return Heart;
      default: return Activity;
    }
  };

  const getEffectivenessColor = (rating: number) => {
    if (rating >= 8) return 'text-green-600';
    if (rating >= 6) return 'text-yellow-600';
    if (rating >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const activeCount = treatments.filter(t => t.isActive).length;
  const avgEffectiveness = treatments.reduce((sum, t) => sum + (t.effectivenessRating || 0), 0) / treatments.length;
  const totalSessions = treatments.reduce((sum, t) => sum + t.totalSessions, 0);
  const monthlyCost = treatments.filter(t => t.cost && t.isActive).reduce((sum, t) => sum + (t.cost || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-slate-800">Holistic Treatment Tracking</h3>
          <p className="text-slate-600 mt-1">Track complementary and alternative treatments alongside conventional care</p>
        </div>
        <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
          <Plus className="h-4 w-4" />
          <span>Add Treatment</span>
        </button>
      </div>

      {/* Treatment Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Active Treatments</p>
              <p className="text-2xl font-bold text-slate-800">{activeCount}</p>
            </div>
            <Heart className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Avg Effectiveness</p>
              <p className={`text-2xl font-bold ${getEffectivenessColor(avgEffectiveness)}`}>
                {avgEffectiveness.toFixed(1)}/10
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Sessions</p>
              <p className="text-2xl font-bold text-slate-800">{totalSessions}</p>
            </div>
            <Calendar className="h-8 w-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Monthly Cost</p>
              <p className="text-2xl font-bold text-slate-800">${monthlyCost}</p>
            </div>
            <Activity className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Treatment Correlations */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <TrendingUp className="h-6 w-6 text-green-600" />
          <h4 className="text-lg font-semibold text-slate-800">Treatment Effectiveness Analysis</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {correlations.map((correlation, index) => (
            <div key={index} className="bg-white rounded-lg p-4 border border-slate-200">
              <h5 className="font-medium text-slate-800 mb-2">{correlation.treatmentName}</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Target:</span>
                  <span className="font-medium">{correlation.symptom}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Improvement:</span>
                  <span className="text-green-600 font-medium">{correlation.improvement}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Timeframe:</span>
                  <span className="font-medium">{correlation.timeframe}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Confidence:</span>
                  <span className="text-blue-600 font-medium">{correlation.confidence}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Treatment List */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h4 className="text-lg font-semibold text-slate-800">Current Treatments</h4>
        </div>
        <div className="divide-y divide-slate-200">
          {treatments.map((treatment) => {
            const CategoryIcon = getCategoryIcon(treatment.category);
            return (
              <div key={treatment.id} className="p-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <CategoryIcon className="h-5 w-5 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h5 className="font-medium text-slate-800">{treatment.name}</h5>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(treatment.category)}`}>
                          {treatment.category.replace('_', ' ')}
                        </span>
                        {!treatment.isActive && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600 mb-3">
                        <div>
                          <span className="font-medium">Target:</span> {treatment.targetCondition}
                        </div>
                        <div>
                          <span className="font-medium">Frequency:</span> {treatment.frequency}
                        </div>
                        <div>
                          <span className="font-medium">Duration:</span> {treatment.duration}
                        </div>
                      </div>

                      {treatment.provider && (
                        <p className="text-sm text-slate-600 mb-2">
                          <span className="font-medium">Provider:</span> {treatment.provider}
                        </p>
                      )}

                      <div className="flex items-center space-x-6 text-sm text-slate-600 mb-2">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>Last: {new Date(treatment.lastSession).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Activity className="h-4 w-4" />
                          <span>{treatment.totalSessions} total sessions</span>
                        </div>
                        {treatment.cost && (
                          <div className="flex items-center space-x-2">
                            <span>${treatment.cost}/session</span>
                          </div>
                        )}
                      </div>

                      {treatment.effectivenessRating && (
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm text-slate-600">Effectiveness:</span>
                          <div className="flex items-center space-x-1">
                            {[...Array(10)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-2 h-2 rounded-full ${
                                  i < treatment.effectivenessRating!
                                    ? 'bg-green-500'
                                    : 'bg-slate-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className={`text-sm font-medium ${getEffectivenessColor(treatment.effectivenessRating)}`}>
                            {treatment.effectivenessRating}/10
                          </span>
                        </div>
                      )}

                      {treatment.notes && (
                        <p className="text-sm text-slate-600 italic">{treatment.notes}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      Log Session
                    </button>
                    <button className="px-3 py-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Treatment Categories */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">Available Treatment Categories</h4>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
          {[
            { category: 'meditation', icon: '🧘', label: 'Meditation' },
            { category: 'acupuncture', icon: '🪡', label: 'Acupuncture' },
            { category: 'massage', icon: '💆', label: 'Massage' },
            { category: 'yoga', icon: '🧘‍♀️', label: 'Yoga' },
            { category: 'supplements', icon: '💊', label: 'Supplements' },
            { category: 'exercise', icon: '🏃', label: 'Exercise' },
            { category: 'diet', icon: '🥗', label: 'Diet Plans' },
            { category: 'sleep', icon: '😴', label: 'Sleep Hygiene' },
            { category: 'stress_management', icon: '🌱', label: 'Stress Management' }
          ].map((item) => (
            <button
              key={item.category}
              className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="text-xl mb-1">{item.icon}</div>
              <p className="text-xs font-medium text-slate-800">{item.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Provider Talking Points */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">Healthcare Provider Discussion Points</h4>
        <div className="space-y-3 text-sm">
          <div className="p-3 bg-white rounded-lg border-l-4 border-green-500">
            <p className="font-medium text-slate-800 mb-1">Meditation for Anxiety Management</p>
            <p className="text-slate-600">
              "I've been practicing daily meditation for 6 weeks and have seen a 45% reduction in my anxiety levels. 
              Could this complement my current treatment plan?"
            </p>
          </div>
          <div className="p-3 bg-white rounded-lg border-l-4 border-blue-500">
            <p className="font-medium text-slate-800 mb-1">Acupuncture for Chronic Pain</p>
            <p className="text-slate-600">
              "I've had 8 acupuncture sessions and experienced a 60% reduction in back pain intensity. 
              Should I continue this alongside conventional pain management?"
            </p>
          </div>
          <div className="p-3 bg-white rounded-lg border-l-4 border-purple-500">
            <p className="font-medium text-slate-800 mb-1">Sleep Improvement Techniques</p>
            <p className="text-slate-600">
              "Progressive muscle relaxation has improved my sleep quality by 70% over 4 weeks. 
              How does this align with addressing my insomnia?"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
